<template>
  <div
    class="chatbot-widget"
    :class="{ docked: isOpen }"
  >
    <!-- Toggle Button -->
    <button
      v-if="!isOpen"
      class="chatbot-toggle"
      aria-label="Open chat assistant"
      @click="toggleChat"
    >
      <i class="pi pi-comments" />
    </button>

    <!-- Chat Panel (Docked on Right) -->
    <Transition name="chatbot-slide">
      <div
        v-if="isOpen"
        class="chatbot-panel"
      >
        <!-- Resize Handle -->
        <div
          class="resize-handle"
          @mousedown="startResize"
        />

        <!-- Header -->
        <div class="chatbot-header">
          <div class="chatbot-header-left">
            <i class="pi pi-android text-xl" />
            <span class="chatbot-title">Deep Thought</span>
          </div>
          <div class="chatbot-header-actions">
            <button
              v-tooltip.left="'Clear chat'"
              class="chatbot-action-btn"
              aria-label="Clear chat history"
              @click="clearChat"
            >
              <i class="pi pi-trash" />
            </button>
            <button
              class="chatbot-action-btn"
              aria-label="Close chat"
              @click="closeChat"
            >
              <i class="pi pi-times" />
            </button>
          </div>
        </div>

        <!-- Messages -->
        <ChatbotMessages />

        <!-- Input -->
        <ChatbotInput />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const { isOpen, toggleChat, closeChat, clearChat } = useChatbot()

const panelWidth = ref(400)
const isResizing = ref(false)
const MIN_WIDTH = 320
const MAX_WIDTH = 800

function startResize(e: MouseEvent) {
  isResizing.value = true
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
  e.preventDefault()
}

function onResize(e: MouseEvent) {
  if (!isResizing.value) return
  const newWidth = window.innerWidth - e.clientX
  panelWidth.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth))
}

function stopResize() {
  isResizing.value = false
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
  // Save preference
  localStorage.setItem('chatbot-width', String(panelWidth.value))
}

onMounted(() => {
  const saved = localStorage.getItem('chatbot-width')
  if (saved) {
    panelWidth.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parseInt(saved)))
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<style scoped>
.chatbot-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  font-family: inherit;
}

.chatbot-widget.docked {
  bottom: 0;
  right: 0;
  top: 0;
}

/* Toggle Button - hidden on mobile (uses TopBar button instead) */
.chatbot-toggle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-700));
  border: none;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.3),
    0 0 20px rgba(57, 255, 20, 0.3);
  transition: all 0.3s ease;
}

@media (min-width: 481px) {
  .chatbot-toggle {
    display: flex;
  }
}

.chatbot-toggle:hover {
  transform: scale(1.05);
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.4),
    0 0 30px rgba(57, 255, 20, 0.5);
}

.chatbot-toggle i {
  font-size: 1.5rem;
  color: var(--p-surface-900);
}

/* Chat Panel - Docked Right */
.chatbot-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: v-bind('panelWidth + "px"');
  max-width: 90vw;
  background: var(--p-surface-800);
  border-left: 1px solid rgba(57, 255, 20, 0.2);
  box-shadow:
    -4px 0 24px rgba(0, 0, 0, 0.4),
    0 0 40px rgba(57, 255, 20, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Resize Handle */
.resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  background: transparent;
  transition: background 0.2s;
  z-index: 10;
}

.resize-handle:hover,
.resize-handle:active {
  background: var(--p-primary-500);
}

/* Header */
.chatbot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, var(--p-surface-900), var(--p-surface-800));
  border-bottom: 1px solid rgba(57, 255, 20, 0.2);
  flex-shrink: 0;
}

.chatbot-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--p-primary-500);
}

.chatbot-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--p-surface-0);
}

.chatbot-header-actions {
  display: flex;
  gap: 4px;
}

.chatbot-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--p-surface-400);
  transition: all 0.2s ease;
}

.chatbot-action-btn:hover {
  background: var(--p-surface-700);
  color: var(--p-primary-500);
}

/* Transition animation */
.chatbot-slide-enter-active,
.chatbot-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chatbot-slide-enter-from,
.chatbot-slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .chatbot-widget {
    bottom: 16px;
    right: 16px;
  }

  .chatbot-widget.docked {
    bottom: 0;
    right: 0;
    left: 0;
    top: auto;
  }

  .chatbot-panel {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    height: 80vh;
    border-left: none;
    border-top: 1px solid rgba(57, 255, 20, 0.2);
    border-radius: 16px 16px 0 0;
  }

  .resize-handle {
    display: none;
  }

  .chatbot-toggle {
    width: 48px;
    height: 48px;
  }

  .chatbot-toggle i {
    font-size: 1.25rem;
  }
}
</style>
